import {riskTypes, riskSubTypeIds} from '../../../util/uiconstants';
import {isNullOrUndefined} from '../../../util/utils';

/**
 * Predefined risk filters.
 */
export const RiskFilters = {
	/**
	 * Common condition: risk is not rejected.
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk is not rejected, false otherwise.
	 */
	NotRejected: (risk) => !risk.isRejected,

	/**
	 * Filter for determining if a risk is marked as higher risk.
	 * Significant (riskTypeId=1) and Fraud (riskTypeId=2) risks are always higher risk by definition,
	 * regardless of the persisted riskIsHigherRisk value (which may be incorrect due to a prior bug).
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk is marked as higher risk, false otherwise.
	 */
	IsHigherRisk: (risk) => {
		if (!risk) return false;
		const isHigher = !isNullOrUndefined(risk.riskIsHigherRisk) ? risk.riskIsHigherRisk : risk.isHigherRisk;
		if (!isHigher && (risk.riskTypeId === riskTypes.SIGNIFICANTRISK || risk.riskTypeId === riskTypes.FRAUDRISK)) {
			return true;
		}
		return isHigher;
	},

	// ==================== BASE TYPE FILTERS ====================

	/**
	 * Filter for SignificantRisk (RiskTypeId = 1).
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk is of type SignificantRisk, false otherwise.
	 */
	SignificantRisk: (risk) =>
		risk.riskTypeId === riskTypes.SIGNIFICANTRISK && (risk.riskSubTypeId === risk.riskTypeId || risk.riskSubTypeId === null) && RiskFilters.NotRejected(risk),

	/**
	 * Filter for FraudRisk (RiskTypeId = 2).
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk is of type FraudRisk, false otherwise.
	 */
	FraudRisk: (risk) =>
		risk.riskTypeId === riskTypes.FRAUDRISK && (risk.riskSubTypeId === risk.riskTypeId || risk.riskSubTypeId === null) && RiskFilters.NotRejected(risk),

	/**
	 * Filter for ANY InherentRisk type (RiskTypeId = 3).
	 * This includes: Plain Inherent, AROMM, SROMM, PRROMM, ESROMM - ALL have RiskTypeId = 3.
	 * Use more specific filters below to target specific subtypes.
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk has RiskTypeId = 3, false otherwise.
	 */
	AnyInherentRisk: (risk) => risk.riskTypeId === riskTypes.INHERENTRISK && RiskFilters.NotRejected(risk),

	// ==================== PLAIN INHERENT RISK (NO ROMM SUBTYPE) ====================

	/**
	 * Filter for Plain/Base Inherent Risk ONLY (RiskTypeId = 3, SubTypeId = 3 or NULL).
	 * This excludes AROMM, SROMM, PRROMM, ESROMM.
	 * Matches both higher and lower plain inherent risks.
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk is a plain inherent risk, false otherwise.
	 */
	PlainInherentRisk: (risk) => RiskFilters.AnyInherentRisk(risk) && (risk.riskSubTypeId === riskTypes.INHERENTRISK || risk.riskSubTypeId === null),

	/**
	 * Filter for Plain Inherent Risk - Lower Risk (RiskTypeId = 3, SubTypeId = 3 or NULL, isHigher = 0).
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk is a lower plain inherent risk, false otherwise.
	 */
	PlainInherentRiskLower: (risk) => RiskFilters.PlainInherentRisk(risk) && !RiskFilters.IsHigherRisk(risk),

	/**
	 * Filter for Plain Inherent Risk - Higher Risk (RiskTypeId = 3, SubTypeId = 3 or NULL, isHigher = 1).
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk is a higher plain inherent risk, false otherwise.
	 */
	PlainInherentRiskHigher: (risk) => RiskFilters.PlainInherentRisk(risk) && RiskFilters.IsHigherRisk(risk),

	// ==================== ROMM SUBTYPES (ALL HAVE RiskTypeId = 3) ====================

	/**
	 * Filter for ANY Attribute ROMM (AROMM) - both higher and lower (RiskTypeId = 3, SubTypeId = 9).
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk is an AROMM, false otherwise.
	 */
	AttributeROMM: (risk) => RiskFilters.AnyInherentRisk(risk) && risk.riskSubTypeId === riskSubTypeIds.AttributeROMM,

	/**
	 * Filter for Attribute ROMM - Lower Risk (RiskTypeId = 3, SubTypeId = 9, isHigher = 0).
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk is a lower AROMM, false otherwise.
	 */
	AttributeROMMLower: (risk) => RiskFilters.AttributeROMM(risk) && !RiskFilters.IsHigherRisk(risk),

	/**
	 * Filter for Attribute ROMM - Higher Risk (RiskTypeId = 3, SubTypeId = 9, isHigher = 1).
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk is a higher AROMM, false otherwise.
	 */
	AttributeROMMHigher: (risk) => RiskFilters.AttributeROMM(risk) && RiskFilters.IsHigherRisk(risk),

	/**
	 * Filter for ANY Standard ROMM (SROMM) - both higher and lower (RiskTypeId = 3, SubTypeId = 10).
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk is a SROMM, false otherwise.
	 */
	StandardROMM: (risk) => RiskFilters.AnyInherentRisk(risk) && risk.riskSubTypeId === riskSubTypeIds.StandardROMM,

	/**
	 * Filter for Standard ROMM - Lower Risk (RiskTypeId = 3, SubTypeId = 10, isHigher = 0).
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk is a lower SROMM, false otherwise.
	 */
	StandardROMMLower: (risk) => RiskFilters.StandardROMM(risk) && !RiskFilters.IsHigherRisk(risk),

	/**
	 * Filter for Standard ROMM - Higher Risk (RiskTypeId = 3, SubTypeId = 10, isHigher = 1).
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk is a higher SROMM, false otherwise.
	 */
	StandardROMMHigher: (risk) => RiskFilters.StandardROMM(risk) && RiskFilters.IsHigherRisk(risk),

	/**
	 * Filter for ANY Pre-rejected Standard ROMM (PRROMM) - both higher and lower (RiskTypeId = 3, SubTypeId = 11).
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk is a PRROMM, false otherwise.
	 */
	PreRejectedStandardROMM: (risk) => RiskFilters.AnyInherentRisk(risk) && risk.riskSubTypeId === riskSubTypeIds.PreRejectedStandardROMM,

	/**
	 * Filter for Pre-rejected Standard ROMM - Lower Risk (RiskTypeId = 3, SubTypeId = 11, isHigher = 0).
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk is a lower PRROMM, false otherwise.
	 */
	PreRejectedStandardROMMLower: (risk) => RiskFilters.PreRejectedStandardROMM(risk) && !RiskFilters.IsHigherRisk(risk),

	/**
	 * Filter for Pre-rejected Standard ROMM - Higher Risk (RiskTypeId = 3, SubTypeId = 11, isHigher = 1).
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk is a higher PRROMM, false otherwise.
	 */
	PreRejectedStandardROMMHigher: (risk) => RiskFilters.PreRejectedStandardROMM(risk) && RiskFilters.IsHigherRisk(risk),

	/**
	 * Filter for ANY Entity Specific ROMM (ESROMM) - both higher and lower (RiskTypeId = 3, SubTypeId = 12).
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk is an ESROMM, false otherwise.
	 */
	EntitySpecificROMM: (risk) => RiskFilters.AnyInherentRisk(risk) && risk.riskSubTypeId === riskSubTypeIds.EntitySpecificROMM,

	/**
	 * Filter for Entity Specific ROMM - Lower Risk (RiskTypeId = 3, SubTypeId = 11, isHigher = 0).
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk is a lower ESROMM, false otherwise.
	 */
	EntitySpecificROMMLower: (risk) => RiskFilters.EntitySpecificROMM(risk) && !RiskFilters.IsHigherRisk(risk),

	/**
	 * Filter for Entity Specific ROMM - Higher Risk (RiskTypeId = 3, SubTypeId = 11, isHigher = 1).
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk is a higher ESROMM, false otherwise.
	 */
	EntitySpecificROMMHigher: (risk) => RiskFilters.EntitySpecificROMM(risk) && RiskFilters.IsHigherRisk(risk),

	// ==================== COMPOSITE FILTERS ====================

	/**
	 * Filter for upfront risks: Significant, Fraud and Plain Inherent Risk (both higher and lower).
	 * Does NOT include ROMM variants (AROMM, SROMM, etc.).
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk is an upfront risk, false otherwise.
	 */
	UpfrontRisk: (risk) => RiskFilters.SignificantRisk(risk) || RiskFilters.FraudRisk(risk) || RiskFilters.PlainInherentRisk(risk),

	/**
	 * Filter for higher upfront risks: Significant, Fraud and Higher Plain Inherent Risks.
	 * Does NOT include higher ROMM variants.
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk is a higher upfront risk, false otherwise.
	 */
	HigherUpfrontRisks: (risk) => RiskFilters.SignificantRisk(risk) || RiskFilters.FraudRisk(risk) || RiskFilters.PlainInherentRiskHigher(risk),

	/**
	 * Filter for ALL higher risks (isHigher = 1): Significant, Fraud, Higher Plain Inherent,
	 * and ALL Higher ROMM variants (AROMM, SROMM, PRROMM, ESROMM).
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk is marked as higher, false otherwise.
	 */
	HigherRisks: (risk) =>
		RiskFilters.SignificantRisk(risk) || RiskFilters.FraudRisk(risk) || (RiskFilters.AnyInherentRisk(risk) && RiskFilters.IsHigherRisk(risk)),

	/**
	 * Filter for higher risks excluding Significant and Fraud.
	 * This includes: Higher Plain Inherent and ALL Higher ROMM variants (AROMM, SROMM, PRROMM, ESROMM).
	 * Used for grouping "Higher Risk" category in UI.
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk is a higher inherent risk (not S or F), false otherwise.
	 */
	HigherAnyInherentRisk: (risk) => RiskFilters.AnyInherentRisk(risk) && RiskFilters.IsHigherRisk(risk),

	/**
	 * Filter for ALL lower risks (isHigher = 0): Lower Plain Inherent and ALL Lower ROMM variants.
	 * Does NOT include Significant or Fraud (which are always "higher" by nature).
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk is marked as lower, false otherwise.
	 */
	LowerAnyInherentRisks: (risk) => RiskFilters.AnyInherentRisk(risk) && !RiskFilters.IsHigherRisk(risk),

	/**
	 * Filter for AROMM or SROMM risks (any higher/lower combination).
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk is an AROMM or SROMM, false otherwise.
	 */
	AROMMorSROMM: (risk) => RiskFilters.AttributeROMM(risk) || RiskFilters.StandardROMM(risk),

	/**
	 * Filter for AROMM or SROMM or ESROMM risks (any higher/lower combination).
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk is an AROMM, SROMM, or ESROMM, false otherwise.
	 */
	AROMMorSROMMorESROMM: (risk) => RiskFilters.AttributeROMM(risk) || RiskFilters.StandardROMM(risk) || RiskFilters.EntitySpecificROMM(risk),

	/**
	 * Filter for AROMM or SROMM or ESROMM or PRROMM risks (any higher/lower combination).
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk is an AROMM, SROMM, ESROMM, or PRROMM, false otherwise.
	 */
	AROMMorSROMMorESROMMorPRROMM: (risk) =>
		RiskFilters.AttributeROMM(risk) || RiskFilters.StandardROMM(risk) || RiskFilters.EntitySpecificROMM(risk) || RiskFilters.PreRejectedStandardROMM(risk),

	/**
	 * Filter for Risk Estimate risks.
	 * @param {Object} risk - The risk object to evaluate.
	 * @returns {boolean} - True if the risk is a Risk Estimate, false otherwise.
	 */
	EstimateRisk: (risk) =>
		RiskFilters.NotRejected(risk) &&
		(risk.riskTypeId === riskTypes.VeryLowRiskEstimate || risk.riskTypeId === riskTypes.LowerRiskEstimate || risk.riskTypeId === riskTypes.HigherRiskEstimate),

	// ==================== DEPRECATED (for backwards compatibility) ====================

	/**
	 * @deprecated Use AnyInherentRisk instead for clarity.
	 * Legacy filter that matches any inherent risk (including all ROMM subtypes).
	 */
	InherentRisk: (risk) => RiskFilters.AnyInherentRisk(risk),

	/**
	 * @deprecated Use PlainInherentRiskHigher instead for clarity.
	 */
	HigherInherentRisk: (risk) => RiskFilters.PlainInherentRiskHigher(risk)
};
